import React, { useState } from "react";
import {
  Button,
  Checkbox,
  DateField,
  DropdownField,
  FormSection,
  MultiSelectField,
  TextField,
  useProblemReport,
  useToast,
  type ProblemReport,
} from "../../../components/ui";
import {
  normaliseEmailInput,
  normalisePhoneInput,
  normalisePincodeInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import { STATE_OPTIONS, citiesOfState } from "../../../lib/locations";
import type { CatalogLookup } from "../../clients/hooks/useCatalogLookups";
import {
  ENQUIRY_LOOKUP_NAMES,
  EnquiryLookups,
} from "../hooks/useEnquiryLookups";
import {
  ENQUIRY_FROM_OPTIONS,
  GENDER_OPTIONS,
  PROFESSION_OPTIONS,
} from "../data/enquiryFormOptions";
import { NewEnquiry } from "../types/enquiries.types";
import {
  MAX_AGE_YEARS,
  NewEnquiryErrors,
  ValidatedField,
  normaliseDobInput,
  normaliseNameInput,
} from "../validation";

/**
 * The validated fields in the order they are laid out, which is the order
 * the first problem has to be looked for in.
 */
const FIELD_ORDER: ValidatedField[] = [
  "title",
  "firstName",
  "lastName",
  "gender",
  "dob",
  "profession",
  "lifestyle",
  "goals",
  "enquiryFrom",
  "state",
  "city",
  "pincode",
  "phone",
  "whatsapp",
  "email",
];

/** Stable id per control, so a failing field can be reached and focused. */
const controlId = (field: ValidatedField) => `enquiry-${field}`;

/**
 * What the DOB calendar will offer, matching the two rules `fieldError`
 * checks for the same field: not in the future, and not more than
 * `MAX_AGE_YEARS` back.
 */
const TODAY = new Date();
const EARLIEST_DOB = new Date(TODAY.getFullYear() - MAX_AGE_YEARS, 0, 1);

interface AddEnquiryFormProps {
  enquiry: NewEnquiry;
  /** Patch merge — every control sends only the key it owns. */
  onChange: (patch: Partial<NewEnquiry>) => void;
  /** Failing fields, keyed by field name. */
  errors: NewEnquiryErrors;
  /**
   * The backend's three lists — titles, goals and life styles — plus the
   * `reload` a failed field offers.
   */
  lookups: EnquiryLookups;
  /** The save is in flight — the action bar says so and refuses a second one. */
  saving?: boolean;
  /**
   * What the submit button says. The edit screen is this same form with the
   * answers already in it, so the label is the only thing that has to tell the
   * two apart — "Save & add" makes a promise an edit does not keep.
   */
  saveLabel?: string;
  onSave: () => void;
  onCancel: () => void;
  /**
   * Filled with this form's own way of pointing at what is missing, so the
   * page above can ask for it — the rail's unsaved prompt does. See
   * `useProblemReport`.
   */
  problems?: ProblemReport;
}

const AddEnquiryForm: React.FC<AddEnquiryFormProps> = ({
  enquiry,
  onChange,
  errors,
  lookups,
  saving = false,
  saveLabel = "Save & add",
  onSave,
  onCancel,
  problems,
}) => {
  const toast = useToast();

  // A field's error only surfaces once it has been visited, so the form does
  // not open covered in red before anything has been typed.
  const [touched, setTouched] = useState<
    Partial<Record<ValidatedField, boolean>>
  >({});

  const visit = (field: ValidatedField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  const errorFor = (field: ValidatedField) =>
    touched[field] ? errors[field] : undefined;

  const valid = Object.keys(errors).length === 0;

  /**
   * A dropdown backed by one of the fetched lists — the same treatment the
   * client form gives its own.
   *
   * A list that has not arrived leaves its field shut, but reachable rather
   * than dead: reaching for it says why it is empty. It does not refetch —
   * recovering from a failure is the Try again button below, which is pressed
   * on purpose and only once.
   *
   * An empty list that came back fine gets its own message rather than the
   * failure's: nothing went wrong, there is simply nothing to pick yet.
   */
  const lookupField = (lookup: CatalogLookup, name: string, sample: string) => {
    const { status, names } = lookup;
    const placeholder = status === "loading" ? "Loading…" : `eg. ${sample}`;

    if (status === "ready" && names.length > 0) {
      return { options: names, placeholder };
    }

    return {
      options: names,
      placeholder,
      disabled: true,
      onDisabledClick: () => {
        if (status === "loading") {
          toast.info("Still loading", `The ${name} list is on its way.`);
          return;
        }
        if (status === "failed") {
          toast.warning(
            `Could not load the ${name} list`,
            "Use Try again at the top of the form."
          );
          return;
        }
        toast.info(
          `No ${name} options yet`,
          "Nothing has been set up to pick from."
        );
      },
    };
  };

  // The three are loaded by one pass when the page opens, so they fail
  // together far more often than singly — one banner for the form rather than
  // a retry hidden behind each field that happens to be empty.
  const failed = ENQUIRY_LOOKUP_NAMES.filter(
    (name) => lookups[name].status === "failed"
  );

  // While the numbers are linked, editing the phone has to carry the
  // WhatsApp number with it — otherwise the two drift apart behind a ticked
  // box.
  const handlePhoneChange = (raw: string) => {
    const phone = normalisePhoneInput(raw);
    onChange(
      enquiry.whatsappSameAsPhone ? { phone, whatsapp: phone } : { phone }
    );
  };

  // Unticking leaves the copied number in place rather than blanking it: it
  // is almost always the right starting point for the edit that follows.
  const handleSameAsPhone = (checked: boolean) =>
    onChange(
      checked
        ? { whatsappSameAsPhone: true, whatsapp: enquiry.phone }
        : { whatsappSameAsPhone: false }
    );

  // The city list belongs to the state, so changing the state drops
  // whatever city was picked under the old one.
  const handleStateChange = (state: string) => onChange({ state, city: "" });

  /**
   * Every failing field reports itself at once, and the page moves to the
   * first of them and puts the cursor in it.
   */
  const reportProblems = () => {
    const firstBad = FIELD_ORDER.find((field) => errors[field]);
    if (!firstBad) return;

    setTouched(Object.fromEntries(FIELD_ORDER.map((field) => [field, true])));

    const control = document.getElementById(controlId(firstBad));
    control?.focus({ preventScroll: true });
    control?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  // Also reachable from the page above, for the one press that is not made on
  // this form: the rail's unsaved prompt offering to save it.
  useProblemReport(problems, reportProblems);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Enter in a field reaches this even while the button is off, so the
    // incomplete form is marked up here too rather than silently ignored.
    if (!valid) {
      reportProblems();
      return;
    }

    onSave();
  };

  return (
    <form className="mt-5 flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
      <div className="flex flex-1 flex-col gap-5 pb-5">
        {/* The one place the form asks for its lists again. Shown only when
            something failed, so the usual path never sees it — and pressed on
            purpose, which is what keeps the calls off every click. */}
        {failed.length > 0 && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#F5C2C2] bg-[#FDECEC] px-4 py-3"
          >
            <p className="text-[13px] 3xl:text-[14px] text-[#8A2B2B]">
              {failed.length === ENQUIRY_LOOKUP_NAMES.length
                ? "The form's options could not be loaded, so some fields have nothing to pick from."
                : "Some of the form's options could not be loaded, so those fields have nothing to pick from."}
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={lookups.reload}
            >
              Try again
            </Button>
          </div>
        )}

        <FormSection title="Basic details">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)]">
            <DropdownField
              label="Title"
              id={controlId("title")}
              {...lookupField(lookups.titles, "title", "Ms")}
              value={enquiry.title}
              error={errorFor("title")}
              onBlur={visit("title")}
              onValueChange={(title) => onChange({ title })}
            />
            <TextField
              label="First name"
              id={controlId("firstName")}
              placeholder="eg. Riya"
              autoComplete="given-name"
              required
              value={enquiry.firstName}
              error={errorFor("firstName")}
              onBlur={visit("firstName")}
              onValueChange={(firstName) =>
                onChange({ firstName: toNameCase(normaliseNameInput(firstName)) })
              }
            />
            <TextField
              label="Last name"
              id={controlId("lastName")}
              placeholder="eg. Roy"
              autoComplete="family-name"
              required
              value={enquiry.lastName}
              error={errorFor("lastName")}
              onBlur={visit("lastName")}
              onValueChange={(lastName) =>
                onChange({ lastName: toNameCase(normaliseNameInput(lastName)) })
              }
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
            <DropdownField
              label="Gender"
              id={controlId("gender")}
              placeholder="eg. Male"
              options={GENDER_OPTIONS}
              required
              value={enquiry.gender}
              error={errorFor("gender")}
              onBlur={visit("gender")}
              onValueChange={(gender) => onChange({ gender })}
            />
            <DateField
              label="DOB"
              id={controlId("dob")}
              value={enquiry.dob}
              normalise={normaliseDobInput}
              minDate={EARLIEST_DOB}
              maxDate={TODAY}
              error={errorFor("dob")}
              onBlur={visit("dob")}
              onValueChange={(dob) => onChange({ dob })}
            />
            <DropdownField
              label="Profession"
              id={controlId("profession")}
              placeholder="eg. Working"
              options={PROFESSION_OPTIONS}
              value={enquiry.profession}
              error={errorFor("profession")}
              onBlur={visit("profession")}
              onValueChange={(profession) => onChange({ profession })}
            />
            <DropdownField
              label="Life style"
              id={controlId("lifestyle")}
              {...lookupField(lookups.lifeStyles, "life style", "Semi active")}
              required
              value={enquiry.lifestyle}
              error={errorFor("lifestyle")}
              onBlur={visit("lifestyle")}
              onValueChange={(lifestyle) => onChange({ lifestyle })}
            />
          </div>
        </FormSection>

        <FormSection title="Requirements">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
            {/* Several at once, the way the client form's own Goal field is:
                an enquiry may be raised about more than one thing, and the
                record carries them as a list. At least one is required — an
                enquiry with no goal is not one anybody can be quoted for. */}
            <MultiSelectField
              label="Goal"
              id={controlId("goals")}
              {...lookupField(lookups.serviceGoals, "goal", "Weight loss")}
              required
              values={enquiry.goals}
              error={errorFor("goals")}
              onBlur={visit("goals")}
              onValuesChange={(goals) => onChange({ goals })}
            />
            <DropdownField
              label="Enquiry from"
              id={controlId("enquiryFrom")}
              placeholder="eg. Call"
              options={ENQUIRY_FROM_OPTIONS}
              value={enquiry.enquiryFrom}
              error={errorFor("enquiryFrom")}
              onBlur={visit("enquiryFrom")}
              onValueChange={(enquiryFrom) => onChange({ enquiryFrom })}
            />
          </div>
        </FormSection>

        <FormSection title="Communication details">
          <TextField
            label="Full address"
            placeholder="1202, Galaxy apartment, SV Road"
            autoComplete="street-address"
            value={enquiry.address}
            onValueChange={(address) => onChange({ address })}
          />

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
            {/* State leads City, because it is what fills it. Laid out the
                other way round the reader meets a dead control before the one
                that brings it to life.

                Both lists are typed into rather than scrolled: 34 states and
                up to 120 cities are more than anyone wants to hunt through. */}
            <DropdownField
              label="State"
              id={controlId("state")}
              placeholder="eg. Maharashtra"
              options={STATE_OPTIONS}
              searchable
              value={enquiry.state}
              error={errorFor("state")}
              onBlur={visit("state")}
              onValueChange={handleStateChange}
            />
            <DropdownField
              label="City"
              id={controlId("city")}
              placeholder={enquiry.state ? "eg. Mumbai" : "Select a state first"}
              options={citiesOfState(enquiry.state)}
              disabled={!enquiry.state}
              // Reaching for the city before there is a state to fill it is a
              // reasonable thing to do, so it gets an answer rather than a
              // control that simply refuses to respond.
              onDisabledClick={() =>
                toast.warning(
                  "Select a state first",
                  "The city list is filled by the state you pick."
                )
              }
              searchable
              // The list covers the common cases and cannot claim to be
              // exhaustive: a town it has never heard of is still where an
              // enquiry came from. Typing one offers it as a "Use …" row under
              // whatever matched, and a value already saved that way shows in
              // the closed control rather than reading as blank.
              creatable
              // Still compulsory although the State above it is not, which is
              // the one place this form asks for something behind a field it
              // does not insist on. The disabled press above is what carries
              // it: reaching for the city says the state is what fills it, so
              // the order is discovered rather than demanded.
              required
              value={enquiry.city}
              error={errorFor("city")}
              onBlur={visit("city")}
              onValueChange={(city) => onChange({ city })}
            />
            <TextField
              label="Pincode"
              id={controlId("pincode")}
              placeholder="400130"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              value={enquiry.pincode}
              error={errorFor("pincode")}
              onBlur={visit("pincode")}
              onValueChange={(pincode) =>
                onChange({ pincode: normalisePincodeInput(pincode) })
              }
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
            <TextField
              label="Phone number"
              id={controlId("phone")}
              placeholder=" 7869 369 854"
              prefix="+91"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              required
              value={enquiry.phone}
              error={errorFor("phone")}
              onBlur={visit("phone")}
              onValueChange={handlePhoneChange}
            />
            <div>
              <TextField
                label="WhatsApp number"
                id={controlId("whatsapp")}
                placeholder=" 7869 369 854"
                prefix="+91"
                inputMode="numeric"
                maxLength={10}
                disabled={enquiry.whatsappSameAsPhone}
                value={enquiry.whatsapp}
                error={errorFor("whatsapp")}
                onBlur={visit("whatsapp")}
                onValueChange={(whatsapp) =>
                  onChange({ whatsapp: normalisePhoneInput(whatsapp) })
                }
              />
              <Checkbox
                label="Same as phone number"
                className="mt-2"
                checked={enquiry.whatsappSameAsPhone}
                onCheckedChange={handleSameAsPhone}
              />
            </div>
            <TextField
              label="Email address"
              id={controlId("email")}
              type="email"
              placeholder="eg. riyaroy@gmail.com"
              autoComplete="email"
              required
              value={enquiry.email}
              error={errorFor("email")}
              onBlur={visit("email")}
              onValueChange={(email) =>
                onChange({ email: normaliseEmailInput(email) })
              }
            />
          </div>
        </FormSection>
      </div>

      {/* Run out to the shell's edges the way the design has it, and sticky on
          the foot of the screen now that the page is what scrolls — see the
          same treatment on `AddClientForm`. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-5 flex-shrink-0 border-t border-line bg-white px-4 py-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 3xl:-mx-12 3xl:px-12">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={saving}
            className="min-w-[120px]"
          >
            Cancel
          </Button>
          {/* Off until every required field is filled, and off the soft way
              while one is missing so a press still marks up what is — the same
              bargain as `AddClientForm`, where it is spelled out. */}
          <Button
            type={valid ? "submit" : "button"}
            size="md"
            aria-invalid={valid ? undefined : true}
            disabled={!valid || saving}
            onDisabledClick={!valid && !saving ? reportProblems : undefined}
            className="min-w-[140px]"
          >
            {saving ? "Saving…" : saveLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddEnquiryForm;
